/**
 * How a survey names its partner teams to the people answering, the same way
 * the backend's email and form do: by what each team supports, "IT SF
 * (Finance, DBS, CCD Back Office)", never by its lead. Only the email's list
 * of every team ({partners}) says who leads each.
 */
import type { SurveyArea, SurveyDepartment, SurveyDesign, SurveyPartner } from '../types'

/** "CCD Back Office": an area of a split department, as people know it. */
export const areaName = (department: SurveyDepartment, area: SurveyArea) => `${department.name} ${area.name}`

/** What a partner team supports: whole departments first, then the areas of
 *  split departments. */
export function servedBy(design: SurveyDesign, partnerId: string): string[] {
  const whole = design.departments.filter(d => !d.areas?.length && d.partnerId === partnerId).map(d => d.name)
  const parts = design.departments.flatMap(d => (d.areas ?? []).filter(a => a.partnerId === partnerId).map(a => areaName(d, a)))
  return [...whole, ...parts]
}

/** A partner team named by what it supports, or alone if nothing. */
export function teamLabel(design: SurveyDesign, partner: SurveyPartner): string {
  const served = servedBy(design, partner.id)
  return served.length ? `${partner.name} (${served.join(', ')})` : partner.name
}

/** What {partner} becomes for someone in a department (and area): its
 *  partner team, or the fallback ("IT") when it has none. In a split
 *  department before an area is chosen, every team it could be about. */
export function partnerLabel(design: SurveyDesign, departmentId: string, areaId?: string): string {
  const department = design.departments.find(d => d.id === departmentId)
  const fallback = design.partnerFallback || 'IT'
  const areas = department?.areas ?? []
  if (areas.length && !areaId) {
    const names = [...new Set(areas.map(a => design.partners.find(p => p.id === a.partnerId)?.name).filter(Boolean))]
    return names.join(' or ') || fallback
  }
  const partnerId = areas.length ? areas.find(a => a.id === areaId)?.partnerId : department?.partnerId
  const partner = design.partners.find(p => p.id === partnerId)
  return partner ? teamLabel(design, partner) : fallback
}

/** The list {partners} becomes in the email: each partner team, its lead and
 *  what it supports. A team that supports no department (End User Services)
 *  is nobody's partner, so it isn't listed. */
export const partnerLines = (design: SurveyDesign) => design.partners
  .map(p => ({ id: p.id, team: p.name, lead: p.lead, supports: servedBy(design, p.id) }))
  .filter(p => p.supports.length > 0)

/** The team a department (or one of its areas) is asked about: its name and
 *  what it supports, or no team and the fallback words. */
export function partnerTeam(design: SurveyDesign, departmentId: string, areaId?: string) {
  const department = design.departments.find(d => d.id === departmentId)
  const areas = department?.areas ?? []
  const partnerId = areas.length ? areas.find(a => a.id === areaId)?.partnerId : department?.partnerId
  const partner = design.partners.find(p => p.id === partnerId)
  return {
    team: partner?.name ?? '',
    supports: partner ? servedBy(design, partner.id) : [],
    partner: partnerLabel(design, departmentId, areaId),
  }
}

/** A split department's areas as the survey offers them. */
export const formAreas = (design: SurveyDesign, departmentId: string) => {
  const department = design.departments.find(d => d.id === departmentId)
  return (department?.areas ?? []).map(a => ({
    id: a.id,
    name: areaName(department!, a),
    definition: a.definition,
    ...partnerTeam(design, departmentId, a.id),
  }))
}
